import { type Ref, useEffect, useImperativeHandle, useRef, useState } from "react";

/**
 * Виджет Cloudflare Turnstile на форме открытой регистрации
 * (stage9-signup-turnstile, Р4). Часть экрана, а не UIKit: он грузит стороннюю
 * службу и знает о форме регистрации.
 */

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Минимум глобального `turnstile`, которым пользуется клиент. */
interface TurnstileRenderOptions {
  sitekey: string;
  theme: "light" | "dark";
  callback: (token: string) => void;
  "expired-callback": () => void;
  "error-callback": () => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

/**
 * Скрипт подгружается один раз на страницу: Cloudflare не разрешает отдавать
 * его со своего адреса. Неудача не запоминается — следующее монтирование
 * виджета пробует снова.
 */
function loadTurnstile(): Promise<TurnstileApi> {
  if (scriptPromise !== null) {
    return scriptPromise;
  }
  const loading = new Promise<TurnstileApi>((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        script.remove();
        reject(new Error("скрипт Turnstile загружен без глобального turnstile"));
      }
    };
    script.onerror = () => {
      script.remove();
      reject(new Error("скрипт Turnstile не загрузился"));
    };
    document.head.append(script);
  });
  scriptPromise = loading;
  loading.catch(() => {
    if (scriptPromise === loading) {
      scriptPromise = null;
    }
  });
  return loading;
}

/** Тема приложения — класс `dark` на корне документа (`useTheme` в UIKit). */
function currentTheme(): "light" | "dark" {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

/** Следит за классом `dark` на корне документа: тема меняется и после монтирования. */
function useAppTheme(): "light" | "dark" {
  const [theme, setTheme] = useState(() =>
    typeof document === "undefined" ? "light" : currentTheme(),
  );
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(currentTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    setTheme(currentTheme());
    return () => observer.disconnect();
  }, []);
  return theme;
}

export interface TurnstileWidgetHandle {
  /** Новая проверка: токен одноразовый. */
  reset: () => void;
}

interface TurnstileWidgetProps {
  siteKey: string;
  /** Токен проверки; `null` — токен сброшен (истёк или виджет сообщил об ошибке). */
  onToken: (token: string | null) => void;
  /** Скрипт не загрузился или виджет сообщил об ошибке: проверка недоступна. */
  onUnavailable: () => void;
  handleRef?: Ref<TurnstileWidgetHandle>;
}

export function TurnstileWidget({
  siteKey,
  onToken,
  onUnavailable,
  handleRef,
}: TurnstileWidgetProps) {
  const theme = useAppTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const apiRef = useRef<TurnstileApi | null>(null);
  const onTokenRef = useRef(onToken);
  const onUnavailableRef = useRef(onUnavailable);
  onTokenRef.current = onToken;
  onUnavailableRef.current = onUnavailable;

  useImperativeHandle(handleRef, () => ({
    reset: () => {
      if (apiRef.current !== null && widgetIdRef.current !== null) {
        apiRef.current.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) {
      return;
    }
    let cancelled = false;
    loadTurnstile().then(
      (api) => {
        if (cancelled) {
          return;
        }
        let widgetId: string | undefined;
        try {
          widgetId = api.render(container, {
            sitekey: siteKey,
            theme,
            callback: (token) => onTokenRef.current(token),
            "expired-callback": () => onTokenRef.current(null),
            "error-callback": () => {
              onTokenRef.current(null);
              onUnavailableRef.current();
            },
          });
        } catch (error) {
          console.error("виджет Turnstile не отрисован", error);
          onUnavailableRef.current();
          return;
        }
        if (widgetId === undefined) {
          onUnavailableRef.current();
          return;
        }
        apiRef.current = api;
        widgetIdRef.current = widgetId;
        // Перерисовка по смене темы выдаёт новый виджет без токена.
        onTokenRef.current(null);
      },
      (error: unknown) => {
        console.error("виджет Turnstile недоступен", error);
        if (!cancelled) {
          onUnavailableRef.current();
        }
      },
    );
    return () => {
      cancelled = true;
      if (apiRef.current !== null && widgetIdRef.current !== null) {
        apiRef.current.remove(widgetIdRef.current);
      }
      apiRef.current = null;
      widgetIdRef.current = null;
    };
  }, [siteKey, theme]);

  return <div ref={containerRef} data-slot="turnstile-widget" />;
}
